/**
 * AppModule configuration for the Angular application.
 *
 * This module initializes key services and configurations required for the app,
 * such as authentication using MSAL (Microsoft Authentication Library), 
 * setting up interceptors for API requests, and loading essential app assets.
 *
 * Key functions included:
 * - MSALInstanceFactory: Initializes the MSAL client with authentication details.
 * - MSALInterceptorConfigFactory: Configures the MSAL interceptor for access token handling.
 * - MSALGuardConfigFactory: Defines configuration for route guards that protect authenticated routes.
 * - initializeAppAssets: Ensures app assets and authentication services are loaded before app starts.
 */



import { CommonModule } from '@angular/common'; // Import CommonModule
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AssetsDataService } from './service/assets-data.service';
import { EntraService } from './service/entra.service';

import { TargetBlankPipe } from './shared/pipes/target-blank.pipe';

import { LandingPageComponent } from './components/landing-page/landing-page.component';
import { ReceivingLogComponent } from './components/receiving/receiving-log/receiving-log.component';
import { LogFormComponent } from './components/receiving/log-form/log-form.component';
import { AddContactComponent } from './components/receiving/log-form/add-contact/add-contact.component';
import {SelectLocationComponent} from './components/shared-components/select-location/select-location.component';
import { TableComponent } from './components/shared-components/table/table.component';
import { IonInputComponent } from './components/shared-components/ion-input/ion-input.component';
import { IonTextAreaComponent } from './components/shared-components/ion-textarea/ion-textarea.component';
import { DatePickerComponent } from './components/shared-components/date-picker/date-picker.component';
import { LoadAllIonicComponent } from './components/load-ionic-components/load-all-ionic.component';
import { ContactListItemComponent } from './components/receiving/log-form/add-contact/add-contact-list-item/contact-list-item.component';
import { PhotoManagerComponent } from './components/shared-components/photo-components/new-log/photo-manager/photo-manager.component';
import { LogPhotoManagerComponent } from './components/shared-components/photo-components/log-table/log-photo-manager/log-photo-manager.component';
import { PhotoViewerModalComponent } from './components/shared-components/photo-components/shared/photo-viewer-modal/photo-viewer-modal.component';
import { PreviewEmailComponent } from './components/shared-components/preview-email/preview-email.component';
import { PreviewEmailModalComponent } from './components/shared-components/preview-email/preview-email-modal/preview-email-modal.component';
import { ReprintLabelsModalComponent } from './components/shared-components/reprint-labels-modal/reprint-labels-modal.component';
import { ItemReprintListComponent } from './components/shared-components/item-reprint-list/item-reprint-list.component';
import { ContainerChipsComponent } from './components/shared-components/container-chips/container-chips.component';
import { ItemsEntryComponent } from './components/shared-components/items-entry/items-entry.component';
import { PwaInstructionsModalComponent } from './components/shared-components/pwa-instructions/pwa-instructions-modal/pwa-instructions-modal.component';
import { OfflineBannerComponent } from './components/shared-components/offline-banner/offline-banner.component';
import { PrintFallbackBannerComponent } from './components/shared-components/print-fallback-banner/print-fallback-banner.component';
import { PrintFallbackModalComponent } from './components/shared-components/print-fallback-modal/print-fallback-modal.component';
import { environment } from '../environments/environment';
import { PreviewLogModalComponent } from './components/receiving/log-form/preview-log-modal/preview-log-modal.component';
import { PhotoGridViewerComponent } from './components/shared-components/photo-components/shared/photo-grid-viewer/photo-grid-viewer.component';
import { PhotoUploaderComponent } from './components/shared-components/photo-components/shared/photo-uploader/photo-uploader.component';
import { FileDropTargetDirective } from './shared/directives/file-drop-target.directive';


import { LaydownLogFormComponent } from './components/laydown-space/laydown-new-log/laydown-log-form.component';  
import { LaydownLogTableComponent } from './components/laydown-space/laydown-log-table/laydown-log-table.component';
import { LaydownLocationGuardComponent } from './components/shared-components/laydown-location-guard/laydown-location-guard.component';

import { SofReceivingPageComponent } from './components/sof-receiving/sof-receiving-page/sof-receiving-page.component';
import { ViewLoggedItemComponent } from './components/receiving/view-logged-item/view-logged-item.component';
import { ReceivingLogEditComponent } from './components/receiving/receiving-log-edit/receiving-log-edit.component';
import { SofQueueComponent } from './components/sof-receiving/sof-queue/sof-queue.component';
import { SofDetailComponent } from './components/sof-receiving/sof-detail/sof-detail.component';
import { PoReportViewerComponent } from './components/sof-receiving/po-report-viewer/po-report-viewer.component';

import { AutocompleteDropdownComponent } from './components/shared-components/autocomplete-dropdown/autocomplete-dropdown.component';
import { CameraTestComponent } from './components/shared-components/camera-test/camera-test.component';
import { PrinterTestComponent } from './components/shared-components/printer-test/printer-test.component';

import { APP_BASE_HREF } from '@angular/common';
import { NgModule, CUSTOM_ELEMENTS_SCHEMA, APP_INITIALIZER } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { CurrencyPipe } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';

import { FormsModule, ReactiveFormsModule } from '@angular/forms';

import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import {IonicModule} from '@ionic/angular';
import { HTTP_INTERCEPTORS } from '@angular/common/http';

// //Required for MSAL
import { IPublicClientApplication, PublicClientApplication, InteractionType, BrowserCacheLocation, LogLevel, AccountInfo, AuthenticationResult } from '@azure/msal-browser';
import { MsalGuard, MsalInterceptor, MsalBroadcastService, MsalInterceptorConfiguration, MsalModule, MsalService, MSAL_GUARD_CONFIG, MSAL_INSTANCE, MSAL_INTERCEPTOR_CONFIG, MsalGuardConfiguration, MsalRedirectComponent } from '@azure/msal-angular';

// ZXing Scanner for QR code/barcode scanning
import { ZXingScannerModule } from '@zxing/ngx-scanner';
import { ServiceWorkerModule } from '@angular/service-worker';



const isIE = window.navigator.userAgent.indexOf('MSIE ') > -1 || window.navigator.userAgent.indexOf('Trident/') > -1;

/**
 * Initializes the MSAL client instance for authentication.
 * 
 * @returns {IPublicClientApplication} The MSAL instance configured with authentication details.
 */
export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      // 'Application (client) ID' of app registration in the Microsoft Entra admin center - this value is a GUID
      clientId: environment.clientId,
      // Full directory URL, in the form of https://login.microsoftonline.com/<tenant>
      authority: environment.authority,
      // Must be the same redirectUri as what was provided in your app registration.
      redirectUri: environment.redirectUri,
      // Post logout redirect URI
      postLogoutRedirectUri: environment.postLogoutRedirectUri
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
      storeAuthStateInCookie: isIE
    }
  });
}

/**
 * Configures the MSAL interceptor for handling access tokens.
 * 
 * @returns {MsalInterceptorConfiguration} Configuration for the MSAL interceptor.
 */

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set('https://graph.microsoft.com/v1.0/me', ['User.Read', 'User.Read.All']);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap
  };
}

/**
 * Configures the MSAL guard for protecting routes and requiring authentication.
 * 
 * @returns {MsalGuardConfiguration} Configuration for the MSAL guard.
 */
export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: ['User.Read', 'User.Read.All']
    }
  };
}


/**
 * Initializes app assets and authentication services before app starts.
 * 
 * @param AssetsDataService The service for loading app assets.
 * @param EntraService The service for initializing MSAL authentication.
 * @returns {Promise<any>} A promise that resolves when app assets and authentication are loaded.
 */
export function initializeAppAssets(AssetsDataService: AssetsDataService, EntraService: EntraService) {
  return () => Promise.all([
    AssetsDataService.GetAppDataAssets(),
    AssetsDataService.GetPrintLabelAssets(),
    EntraService.msalInstanceInit()
  ]);
}







@NgModule({
  declarations: [
    TargetBlankPipe,
    AppComponent,
    LandingPageComponent,
    ReceivingLogComponent,
    LogFormComponent,
    AddContactComponent,
    SelectLocationComponent,
    TableComponent,
    IonInputComponent,
    IonTextAreaComponent,
    DatePickerComponent,
    LoadAllIonicComponent,
    ContactListItemComponent,
    PhotoManagerComponent,
    LogPhotoManagerComponent,
    PhotoViewerModalComponent,
    PhotoGridViewerComponent,
    PreviewEmailComponent,
    PreviewEmailModalComponent,
    ReprintLabelsModalComponent,
    PreviewLogModalComponent,
    PhotoUploaderComponent,

    LaydownLogFormComponent,
    LaydownLogTableComponent,
    LaydownLocationGuardComponent,

    SofReceivingPageComponent,
    SofQueueComponent,
    SofDetailComponent,
    PoReportViewerComponent,
    ViewLoggedItemComponent,
    ReceivingLogEditComponent,

    AutocompleteDropdownComponent,
    CameraTestComponent,
    PrinterTestComponent,


  ],
  imports: [
    BrowserModule,
   AppRoutingModule,
    ReactiveFormsModule,
    FormsModule,
    BrowserAnimationsModule,
    CommonModule,
    ItemReprintListComponent,
    ContainerChipsComponent,
    ItemsEntryComponent,
    // Standalone; imported here so any declared (NgModule) template can drop in
    // <app-pwa-instructions-modal> next to a help/info button. The public
    // /install-help route pulls its own copy of the shared step content.
    PwaInstructionsModalComponent,
    // Standalone; imported here for the one place it is used — the app shell's
    // <app-offline-banner>, which puts it on every route at once.
    OfflineBannerComponent,
    // Standalone; both are mounted once in the app shell — the banner on every
    // route while a print-fallback mode is active, and the modal that sets it
    // from the menu's Settings section.
    PrintFallbackBannerComponent,
    PrintFallbackModalComponent,
    // Standalone; the log-table's photo editor uses it for its per-section drop
    // targets, the same directive the receiving flow's photos step uses.
    FileDropTargetDirective,

  IonicModule.forRoot(),
  HttpClientModule,
  ZXingScannerModule,
  ServiceWorkerModule.register('ngsw-worker.js', {
    // Enable the service worker for the deployed environments (UAT + prod), not
    // local/dev builds. We can't use !isDevMode() here: UAT runs with
    // environment.production = false, so enableProdMode() never runs and
    // isDevMode() would be true, silently disabling the SW (and the PWA update
    // prompt) in UAT. Keying off the explicit env flags avoids that trap.
    enabled: environment.production || environment.uat,
    // Register the ServiceWorker as soon as the application is stable
    // or after 30 seconds (whichever comes first).
    registrationStrategy: 'registerWhenStable:30000'
  }),
  ],
  providers: [
    CurrencyPipe,
    AssetsDataService,
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    { provide: HTTP_INTERCEPTORS, useClass: MsalInterceptor, multi: true },
    { provide: MSAL_INSTANCE, useFactory: MSALInstanceFactory },
    { provide: MSAL_GUARD_CONFIG, useFactory: MSALGuardConfigFactory },
    { provide: MSAL_INTERCEPTOR_CONFIG, useFactory: MSALInterceptorConfigFactory },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    {
      provide: APP_INITIALIZER,
      useFactory: initializeAppAssets,
      deps: [AssetsDataService, EntraService],
      multi: true
    }

  ],
  bootstrap: [AppComponent, MsalRedirectComponent
],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppModule { }
